import React, { useState, useRef, useEffect } from 'react';
import { Info, X, ChevronDown, ChevronUp, Search, CircleCheck } from 'lucide-react';
import { useHistory, useParams } from "react-router-dom";
import Tooltip from '../addentities/Tooltip.js';
import apiHelper from '../../apiHelper';
import CharacterLimitedTextarea from '../../components/CharacterLimitedTextarea';
import DiscardConfirmationModal from '../../components/DiscardConfirmationModal.js';
import UnsavedChangesPrompt from '../../components/UnsavedChangesPrompt.js';
import AlertNotification from '../../components/AlertNotification.js';

// Animated Arrow Icon Component for Parent Category dropdown
const AnimatedArrowIcon = ({ isOpen }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width="18" 
    height="18" 
    viewBox="0 0 18 18" 
    fill="none"
    style={{
      transition: 'transform 0.3s ease',
      transform: isOpen ? 'rotate(0deg)' : 'rotate(180deg)'
    }}
  >
    <path 
      d="M4.5 11.25L9 6.75L13.5 11.25" 
      stroke="#212529" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
);

export default function AddCategory() {
  const history = useHistory();
  const { id } = useParams();
  const [categories, setCategories] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [validationError, setValidationError] = useState([]);
  const [disabled, setDisabled] = useState(false);
  const [discardModalOpen, setDiscardModalOpen] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [formData, setFormData] = useState({
    external_id: '',
    category_name: '',
    parent_category: null,
    status: '1', // Default value
    notes: '',
    prefix: ''
  });

  const [initialFormData, setInitialFormData] = useState({
    external_id: '',
    category_name: '',
    parent_category: null,
    status: '1',
    notes: '',
    prefix: ''
  });

  const [errors, setFormErrors] = useState({
    external_id: '',
    category_name: '',
    parent_category: '',
    status: '',
    notes: '',
    prefix: ''
  });

  const [touchedFields, setTouchedFields] = useState({
    category_name: false,
    external_id: false,
    status: false,
    prefix: false
  });

  const [isTypingId, setIsTypingId] = useState(false);
  const [isTypingPrefix, setIsTypingPrefix] = useState(false);

  const [showAlert, setShowAlert] = useState(false);
  const [alertType, setAlertType] = useState('success');
  const [alertAction, setAlertAction] = useState('update');

  const getFieldDisplayName = (fieldName) => {
    const fieldNames = {
      category_name: 'Category Name',
      external_id: 'ID',
      status: 'Status',
      prefix: 'Prefix'
    };
    return fieldNames[fieldName] || fieldName;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    if (name === 'external_id') {
      setIsTypingId(true);
    } else if (name === 'prefix') {
      setIsTypingPrefix(true);
    }

    if (errors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    if (touchedFields[name] && value && name !== 'external_id' && name !== 'prefix') {
      setFormErrors({ ...errors, [name]: '' });
    }
  };

  const handleChangeWithDirty = (e) => {
    if (!isDirty) {
      setIsDirty(true);
    }
    handleChange(e);
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    
    if (name === 'external_id') {
      setIsTypingId(false);
    } else if (name === 'prefix') {
      setIsTypingPrefix(false);
    }
    
    setTouchedFields((prev) => ({ ...prev, [name]: true }));

    let newErrors = { ...errors };

    if (name === 'category_name') {
      if (!value) {
        newErrors.category_name = 'Category name is required';
      } else {
        const duplicateName = categories.find(
          (cat) => cat.category_name?.toLowerCase() === value.toLowerCase() && cat.category_id !== formData.category_id &&
          (cat.parent_category || null) == (formData.parent_category || null)
        );
        newErrors.category_name = duplicateName ? 'Category Name already exists' : '';
      }
    }
    if (name === 'external_id') {
      if (value) {
        const duplicateId = categories.find(
          (cat) => cat.external_id && String(cat.external_id).toLowerCase() === value.toLowerCase() && cat.category_id !== formData.category_id
        );
        newErrors.external_id = duplicateId ? 'Category ID already exists' : '';
      } else {
        newErrors.external_id = '';
      }
    }
    if (name === 'prefix') {
      if (value && value.trim() !== '') {
        const duplicatePrefix = categories.find(
          (cat) => cat.prefix && 
                   cat.prefix.trim() !== '' && 
                   cat.prefix.toLowerCase() === value.toLowerCase() && 
                   cat.category_id !== formData.category_id
        );
        newErrors.prefix = duplicatePrefix ? 'Prefix already exists' : '';
      } else {
        newErrors.prefix = '';
      }
    }
    if (name === 'status') {
      if (value !== '0' && value !== '1') {
        newErrors.status = 'This field is required!';
      } else {
        newErrors.status = '';
      }
    }

    setFormErrors(newErrors);
  };

  const [allCategories, setAllCategories] = useState([]);
  const [isParentCategoryOpen, setIsParentCategoryOpen] = useState(false);
  const [parentCategorySearch, setParentCategorySearch] = useState('');
  const [selectedParentCategory, setSelectedParentCategory] = useState(null);
  const parentCategoryRef = useRef(null);
  const dropdownRef = useRef(null); // Added separate ref for dropdown

  const toggleParentCategory = () => {
    setIsParentCategoryOpen((prev) => !prev);
  };

  const handleParentCategoryInputChange = (e) => {
    const value = e.target.value;
    setParentCategorySearch(value);
    setSelectedParentCategory(null);
    setFormData({ ...formData, parent_category: null });

    if (!isDirty) {
      setIsDirty(true);
    }

    if (value.trim() && !isParentCategoryOpen) {
      setIsParentCategoryOpen(true);
    }
  };

  // Validate parent category when user clicks outside
  const validateParentCategory = () => {
    // If there's a search value but no selected category, check if it matches any available option
    if (parentCategorySearch.trim() && !selectedParentCategory) {
      const matchingCategory = allCategories.find(cat => {
        if (cat.category_name.toLowerCase() !== parentCategorySearch.toLowerCase()) return false;
        if (cat.category_id === formData.category_id) return false;
        // Exclude descendants — same rule as the dropdown filter
        const hier = cat.category_hierarchy || [];
        if (formData.category_id && hier.some(h => h.id === formData.category_id)) return false;
        return true;
      });

      // If no matching category found (or it was a descendant), clear the field
      if (!matchingCategory) {
        setParentCategorySearch('');
        setSelectedParentCategory(null);
        setFormData({ ...formData, parent_category: null });
      } else {
        // If matching category found, select it
        setParentCategorySearch('');
        setSelectedParentCategory(matchingCategory);
        setFormData({ ...formData, parent_category: matchingCategory.category_id });
      }
    }
  };

  const clearParentCategory = (e) => {
    e.stopPropagation();
    setParentCategorySearch('');
    setSelectedParentCategory(null);
    setFormData({ ...formData, parent_category: null });
    setIsParentCategoryOpen(false);

    if (!isDirty) {
      setIsDirty(true);
    }

    // Re-validate category name with no parent
    if (formData.category_name) {
      const duplicate = categories.find(
        cat => cat.category_name?.toLowerCase() === formData.category_name.toLowerCase()
            && cat.category_id !== formData.category_id
            && (cat.parent_category || null) == null
      );
      setFormErrors(prev => ({ ...prev, category_name: duplicate ? 'Category Name already exists' : (prev.category_name?.includes('already exists') ? '' : prev.category_name) }));
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      // Check if click is outside both the parent category container AND the dropdown
      if (isParentCategoryOpen && 
          parentCategoryRef.current && 
          !parentCategoryRef.current.contains(e.target) &&
          dropdownRef.current && 
          !dropdownRef.current.contains(e.target)) {
        
        // Validate parent category before closing dropdown
        validateParentCategory();
        setIsParentCategoryOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isParentCategoryOpen, parentCategorySearch, selectedParentCategory, allCategories, formData.category_id]);

  const filteredParentCategory = allCategories.filter((d) => {
    // Exclude the category being edited
    if (d.category_id === formData.category_id) return false;

    const hierarchy = d.category_hierarchy || [];

    // Exclude descendants — categories that have the editing category anywhere in their hierarchy
    if (formData.category_id && hierarchy.some(h => h.id === formData.category_id)) return false;

    // Apply search filter
    const term = parentCategorySearch.trim().toLowerCase();
    if (d.category_name.toLowerCase().includes(term)) return true;
    return hierarchy.some(h => h.name.toLowerCase().includes(term));
  });

  const handleParentCategoryCheckbox = (parentcategory) => {
    setSelectedParentCategory(parentcategory);
    setFormData({ ...formData, parent_category: parentcategory.category_id });
    setParentCategorySearch('');
    setIsParentCategoryOpen(false);
    setFormErrors({ ...errors, parent_category: '' });

    if (!isDirty) {
      setIsDirty(true);
    }

    // Re-validate category name with the newly selected parent
    if (formData.category_name) {
      const duplicate = categories.find(
        cat => cat.category_name?.toLowerCase() === formData.category_name.toLowerCase()
            && cat.category_id !== formData.category_id
            && (cat.parent_category || null) == (parentcategory.category_id || null)
      );
      setFormErrors(prev => ({ ...prev, category_name: duplicate ? 'Category Name already exists' : (prev.category_name?.includes('already exists') ? '' : prev.category_name) }));
    }
  };

  // Handle blur event for parent category input
  const handleParentCategoryBlur = (e) => {
    // Use setTimeout to ensure this runs after the click outside handler
    setTimeout(() => {
      validateParentCategory();
    }, 100); // Increased timeout to 100ms
  };

  const [activeTab] = useState("Edit Category");
  const breadcrumbs = ["Product", "Category", activeTab];

  const showAlertNotification = (type, action = 'update') => {
    setAlertType(type);
    setAlertAction(action);
    setShowAlert(true);
  };

  const handleAlertClose = () => {
    setShowAlert(false);
  };

  const handleSave = async () => {
    console.log("Saving category with:", formData);
    console.log(" • Selected Parent Category:", selectedParentCategory);
    setShowAlert(false);
    setDisabled(true);
    setTouchedFields({ category_name: true, external_id: true, status: true, prefix: true });

    let form_errors = { external_id: '', category_name: '', status: '', parent_category: '', prefix: '' };
    let is_error = false;

    if (!formData.category_name) {
      form_errors["category_name"] = "Category name is required";
      is_error = true;
    } else {
      const duplicateName = categories.find(
        (cat) => cat.category_name?.toLowerCase() === formData.category_name.toLowerCase() && cat.category_id !== formData.category_id &&
        (cat.parent_category || null) == (formData.parent_category || null)
      );
      if (duplicateName) {
        form_errors["category_name"] = "Category Name already exists";
        is_error = true;
      }
    }

    if (formData.external_id) {
      const duplicateId = categories.find(
        (cat) => cat.external_id && String(cat.external_id).toLowerCase() === formData.external_id.toLowerCase() && cat.category_id !== formData.category_id
      );
      if (duplicateId) {
        form_errors["external_id"] = "Category ID already exists";
        is_error = true;
      }
    }

    if (formData.prefix && formData.prefix.trim() !== '') {
      const duplicatePrefix = categories.find(
        (cat) => cat.prefix && 
                 cat.prefix.trim() !== '' && 
                 cat.prefix.toLowerCase() === formData.prefix.toLowerCase() && 
                 cat.category_id !== formData.category_id
      );
      if (duplicatePrefix) {
        form_errors["prefix"] = "Prefix already exists";
        is_error = true;
      }
    }

    if (formData.status !== '0' && formData.status !== '1') {
      form_errors["status"] = "This field is required!";
      is_error = true;
    }
    if (formData.external_id && formData.external_id.length > 50) {
        is_error = true;
    }
    setFormErrors(form_errors);
    if (is_error) {
      setDisabled(false);
      showAlertNotification('error', 'update');
      return false;
    }

    try {
    let postFormData = new FormData();
    Object.keys(formData).forEach(key => {
      if (formData[key] !== null) {
        postFormData.append(key, formData[key]);
      }
    });

    const response = await apiHelper(`inventory/categories/update/${id}/`, 'POST', postFormData, 'multipart/form-data');
    setDisabled(false);
    if (response) {
      if (response.ok) {
        setSuccessMessage('Category updated successfully.');
        setValidationError([]);
        setFormErrors({ external_id: '', category_name: '', status: '', parent_category: '', prefix: '' });
        setTouchedFields({ category_name: false, external_id: false, status: false, prefix: false });
        
        setInitialFormData(formData);
        setIsDirty(false);
        
        showAlertNotification('success', 'update');
        
        // setTimeout(() => {
        //   history.push("/category", { 
        //     refresh: true
        //   });
        // }, 1000);

         history.push("/category", { success: "Category Saved Successfully" });
            return;
      } else if (response.status === 400) {
        const response_errors = await response.json();
        let errors = [];
        let new_form_errors = { external_id: '', category_name: '', status: '', parent_category: '', prefix: '' };
        for (let i in response_errors) {
          if (i === 'category_name') {
            new_form_errors[i] = response_errors[i][0].includes('already exists') 
              ? 'Category Name already exists' 
              : response_errors[i][0];
          } else if (i === 'external_id') {
            new_form_errors[i] = response_errors[i][0].includes('already exists') 
              ? 'Category ID already exists' 
              : response_errors[i][0];
          } else if (i === 'prefix') {
            new_form_errors[i] = response_errors[i][0].includes('already exists') 
              ? 'Prefix already exists' 
              : response_errors[i][0];
          } else if (i === 'parent_category') {
            new_form_errors[i] = response_errors[i][0];
          } else {
            errors.push(`${i}: ${response_errors[i][0]}`);
          }
        }
        setFormErrors(new_form_errors);
        setValidationError(errors);
        setSuccessMessage('');
        
        showAlertNotification('error', 'update');
      }
    }
    } catch (error) {
      console.error('Save category error:', error);
    }
  };

  const handleDiscard = () => {
    if (!isDirty) {
      history.push("/category", { 
        success: "Category Discarded Successfully",
        action: "discard",
        refresh: true
      });
    } else {
      setDiscardModalOpen(true);
    }
  };

  const handleDiscardConfirm = (confirm) => {
    if (confirm) {
      setIsDirty(false);
      history.push("/category", { 
        success: "Category Discarded Successfully",
        action: "discard",
        refresh: true
      });
    }
    setDiscardModalOpen(false);
  };

  const handleCancelDiscard = () => {
    setDiscardModalOpen(false);
  };

  useEffect(() => {
    async function fetchCategoriesData() {
      const response = await apiHelper('inventory/categories/?is_archived=false');
      if (response && response.ok) {
        const result = await response.json();
        setAllCategories(result);
        setCategories(result);
      }
    }

    async function fetchCategoryData() {
      const res = await apiHelper(`inventory/categories/show/${id}/`, 'GET');
      if (res.ok) {
        let result = await res.json();
        result = {
          ...result,
          parent_category: result.parent_category || null,
          status: result.status === true || result.status === '1' || result.status === 1 ? '1' : '0',
          prefix: result.prefix || ''
        };
        setFormData(result);
        setInitialFormData(result);
      }
    }

    const loadAll = async () => {
      try {
        await Promise.all([fetchCategoriesData(), fetchCategoryData()]);
      } catch (error) {
        console.error('Failed to load category data:', error);
      }
    };
    loadAll();
  }, [id]);

  useEffect(() => {
    if (formData.parent_category && allCategories.length > 0) {
      const preSelectedCategory = allCategories.find(cat => cat.category_id === formData.parent_category);
      if (preSelectedCategory && !selectedParentCategory) {
        setSelectedParentCategory(preSelectedCategory);
      }
    } else if (!formData.parent_category && selectedParentCategory) {
      setSelectedParentCategory(null);
    }
  }, [formData.parent_category, allCategories, selectedParentCategory]);

  const saveRef = useRef(null);
  const [saveModalOpen, setSaveModalOpen] = useState(false);

  const handleSaveConfirm = () => {
    setSaveModalOpen(false);
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (saveRef.current && !saveRef.current.contains(event.target)) {
        setSaveModalOpen(false);
      }
    }

    if (saveModalOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [saveModalOpen]);

  return (
    <>
      <div className="company-body main-company-body-inner new-look" style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden',
            }}>
        <div className="breadcrumb">
          {breadcrumbs.map((crumb, index) => {
            const isLast = index === breadcrumbs.length - 1;
            return (
              <span
                key={index}
                className={`breadcrumb-item ${crumb === "Category" ? "clickable" : ""}`}
                onClick={() => {
                  if (crumb === "Category") {
                    if (!isDirty) {
                      history.push("/category");
                    } else {
                      setDiscardModalOpen(true);
                    }
                  }
                }}
                style={{ cursor: crumb === "Category" ? "pointer" : "default" }}
              >
                {crumb}
                {!isLast ? "  " : ""}
              </span>
            );
          })}
        </div>

        <div className='edit-organisation-info'>
          <div className='edit-organisation-left'>
            <h1>Edit Category</h1>
          </div>
          <div className='discard-save-organisation-right d-flex align-items-center gap-2'>
            <button className="dark-button" type='button' onClick={handleDiscard}>
              Discard
            </button>
            <DiscardConfirmationModal
              isOpen={discardModalOpen}
              onCancel={handleCancelDiscard}
              onConfirm={handleDiscardConfirm}
              entityName="Category"
            />
            <button className="dark-border" type='button' onClick={handleSave}>
              Save
            </button>
          </div>
        </div>

        {showAlert && (
          <AlertNotification
            type={alertType}
            recordName="Category"
            action={alertAction}
            onClose={handleAlertClose}
          />
        )}
        <div style={{
                    flex: 1,
                    overflowY: 'auto',
                    paddingBottom: '24px',
                }}>
          <div className="info-section">
            <div className="info-card new-info-overflow-auto">
              <div className="info-header">
                <h4 className="info-title">Basic Info</h4>
              </div>
              <div className="info-content d-block">
                <div className='grid-info-adding'>
                  <div className='row gappingcls-added'>
                    <div className='col-lg-6 col-md-6 col-12'>
                      <div className="info-inner">
                        <label htmlFor="categoryName" className="form-label d-flex align-items-center gap-1">
                          Category Name <span className='error-color'>*</span> 
                        </label>
                        <input
                          type="text"
                          className={`form-control common-input-all ${errors.category_name ? 'err-color' : ''}`}
                          id="categoryName"
                          name="category_name"
                          value={formData.category_name}
                          onChange={handleChangeWithDirty}
                          onBlur={handleBlur}
                        />
                        {errors.category_name && <p style={{ color: '#DC3545', fontSize: '12px', marginTop: '4px', marginBottom: '0' }}>{errors.category_name}</p>}
                      </div>
                    </div>
                    <div className='col-lg-6 col-md-6 col-12'>
                      <div className="info-inner">
                        <label htmlFor="status" className="form-label">
                          Status <span className='error-color'>*</span> 
                        </label>
                        <div className='flexing-type-radio'>
                          <div className="form-check">
                            <input
                              className={`form-check-input ${errors.status ? 'err-color' : ''}`}
                              type="radio"
                              id="flexRadioDefault1"
                              name="status"
                              value="1"
                              checked={formData.status === '1'}
                              onChange={handleChangeWithDirty}
                              onBlur={handleBlur}
                            />
                            <label className="form-check-label" htmlFor="flexRadioDefault1" style={{ fontWeight: 'normal', fontSize: '14px' }}>
                              Active
                            </label>
                          </div>
                          <div className="form-check">
                            <input
                              className={`form-check-input ${errors.status ? 'err-color' : ''}`}
                              type="radio"
                              id="flexRadioDefault2"
                              name="status"
                              value="0"
                              checked={formData.status === '0'}
                              onChange={handleChangeWithDirty}
                              onBlur={handleBlur}
                            />
                            <label className="form-check-label" htmlFor="flexRadioDefault2" style={{ fontWeight: 'normal', fontSize: '14px' }}>
                              Inactive
                            </label>
                          </div>
                        </div>
                        {errors.status && <p style={{ color: '#DC3545', fontSize: '12px', marginTop: '4px', marginBottom: '0' }}>{errors.status}</p>}
                      </div>
                    </div>
                  </div>

                  <div className='row gappingcls-added'>
                    <div className='col-lg-6 col-md-6 col-12'>
                      <div className="info-inner">
                        <label htmlFor="categoryId" className="form-label d-flex align-items-center gap-2">
                          ID 
                        </label>
                        <CharacterLimitedTextarea 
                          id="categoryId"
                          name="external_id"
                          value={formData.external_id}
                          onChange={handleChangeWithDirty}
                          onBlur={handleBlur}
                          maxLength={50}
                          counterPosition="end"
                          className={errors.external_id && !isTypingId ? 'err-color' : ''}
                          error={errors.external_id && !isTypingId ? errors.external_id : ''}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="info-card new-info-overflow-auto">
              <div className="info-header">
                <h4 className="info-title">Business Info</h4>
              </div>
              <div className="info-content d-block">
                <div className='grid-info-adding'>
                  <div className='row gappingcls-added'>
                    <div className='col-lg-6 col-md-6 col-12'>
                      <div className="info-inner" style={{ position: 'relative' }} ref={parentCategoryRef}>
                        <label className="form-label d-flex align-items-center gap-2">
                          Parent Category
                          <Tooltip
                            title="Parent Category"
                            body="The main category this category belongs under if any">
                            <Info size={16} className="info-icon" />
                          </Tooltip>
                        </label>
                        <div className="relative" style={{ position: 'relative' }}>
                          <div
                            className="form-control common-input-all d-flex align-items-center cursor-pointer rounded p-2"
                            onClick={() => setIsParentCategoryOpen(!isParentCategoryOpen)}
                            style={{ 
                              height: '39px', 
                              padding: '0 8px',
                              position: 'relative'
                            }}
                          >
                            <input
                              type="text"
                              style={{
                                flex: 1,
                                border: 'none',
                                outline: 'none',
                                background: 'transparent',
                                fontSize: '14px',
                                padding: '0',
                                marginRight: '8px'
                              }}
                              value={selectedParentCategory ? selectedParentCategory.category_name : parentCategorySearch}
                              onChange={handleParentCategoryInputChange}
                              onFocus={() => setIsParentCategoryOpen(true)}
                              onBlur={handleParentCategoryBlur}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!isParentCategoryOpen) {
                                  setIsParentCategoryOpen(true);
                                }
                              }}
                            />
                            <div className="d-flex align-items-center gap-1">
                              {(selectedParentCategory || parentCategorySearch) && (
                                <X 
                                  size={16} 
                                  style={{ cursor: 'pointer', color: '#6c757d' }}
                                  onClick={clearParentCategory}
                                />
                              )}
                              <AnimatedArrowIcon isOpen={isParentCategoryOpen} />
                            </div>
                          </div>

                          {isParentCategoryOpen && (
                            <div
                              ref={dropdownRef}
                              style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                width: '100%',
                                zIndex: 1000,
                                maxHeight: '270px',
                                overflowY: 'auto',
                                marginTop: '4px',
                                background: '#fff',
                                border: '1px solid #e9ecef',
                                borderRadius: '6px',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                boxSizing: 'border-box',
                              }}
                            >
                              {filteredParentCategory.length > 0 ? (
                                filteredParentCategory.map((item) => (
                                  <div
                                    key={item.category_id}
                                    className="hover:bg-gray-100 flex items-center justify-between cursor-pointer"
                                    onClick={() => handleParentCategoryCheckbox(item)}
                                    onMouseDown={(e) => e.preventDefault()}
                                    style={{ padding: '10px', cursor: 'pointer' }}
                                  >
                                    <div>
                                      <div style={{ color: '#212529', fontWeight: '400', fontSize: '14px', fontFamily: 'Montserrat' }}>
                                        {item.category_name}
                                      </div>
                                      {item.category_hierarchy && item.category_hierarchy.length >= 1 && (
                                        <div style={{ fontSize: '12px', color: '#6C757D', fontFamily: 'Montserrat' }}>
                                          {item.category_hierarchy.map(c => c.name).join(' >> ')}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div style={{ width: '100%', padding: '10px 12px', fontFamily: 'Montserrat', fontSize: '14px', color: '#6C757D', textAlign: 'center' }}>
                                  No results found
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        {errors.parent_category && <p style={{ color: '#DC3545', fontSize: '12px', marginTop: '4px', marginBottom: '0' }}>{errors.parent_category}</p>}
                      </div>
                    </div>

                    <div className='col-lg-6 col-md-6 col-12'>
                      <div className="info-inner">
                        <label htmlFor="prefix" className="form-label d-flex align-items-center gap-1">
                          Prefix
                          <Tooltip
                            title="Prefix"
                            body="A short code for the category, used in automating SKU generation">
                            <Info size={16} className="info-icon" />
                          </Tooltip>
                        </label>
                        <input
                          type="text"
                          className={`form-control common-input-all ${errors.prefix && !isTypingPrefix ? 'err-color' : ''}`}
                          id="prefix"
                          name="prefix"
                          value={formData.prefix}
                          onChange={handleChangeWithDirty}
                          onBlur={handleBlur}
                        />
                        {errors.prefix && !isTypingPrefix && <p style={{ color: '#DC3545', fontSize: '12px', marginTop: '4px', marginBottom: '0' }}>{errors.prefix}</p>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="info-card">
              <div className="info-header">
                <h4 className="info-title">Notes</h4>
              </div>
              <div className="info-content">
                <div className="info-inner common-textarea">
                  <textarea
                    className="form-control w-100 common-textarea-all"
                    id="exampleFormControlTextarea1"
                    rows="3"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChangeWithDirty}
                  ></textarea>
                </div>
              </div>
            </div>
          </div>


          {!discardModalOpen && <UnsavedChangesPrompt isDirty={isDirty} entityName={'Category'}/>}

          {saveModalOpen && (
            <>
              <div className="drawer-overlay" />
              <div
                className="entity-drawer modal-popup-cls"
                ref={saveRef}
                style={{
                  maxWidth: 650,
                  margin: "0 auto",
                  background: "white",
                  borderRadius: 12,
                  zIndex: 1050,
                  padding: 24,
                  position: "fixed",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
                  height: "auto",
                  right: "auto",
                  overflow: "inherit",
                }}
              >
                <div className="drawer-header d-flex justify-content-end align-items-center pt-0 pe-0">
                  <button className="close-btn" onClick={() => setSaveModalOpen(false)}>
                    <X size={20} />
                  </button>
                </div>
                <h3 style={{ textAlign: "left", color: "#212529", fontSize: "20px", fontWeight: "600", lineHeight: "24px", fontFamily: "Montserrat", marginBottom: "5px" }}>New feature in Progress</h3>
                <p style={{ marginBottom: "0", textAlign: "left", color: "#6C757D", fontSize: "14px", fontWeight: "400", lineHeight: "20px", fontFamily: "Montserrat" }}>Work in Progress</p>
                <div className="drawer-footer" style={{ paddingTop: "10px" }}>
                  <button className="btn-save" onClick={handleSaveConfirm}>
                    OK
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}